import { useState, useEffect, useMemo } from 'react'
import { MapPin, Loader2, Plus, X } from 'lucide-react'
import { fetchZones, type ZoneSummary } from '../services/adminService'

/**
 * Picks operational zones from the ones the platform actually uses.
 *
 * Zones are derived grid cells (`ZONE_<lat>_<lng>`), not a curated list, so
 * typing them by hand is a reliable way to produce a zone that matches nothing
 * and quietly excludes a collector or company from assignment. This shows the
 * zones that exist, with a real address for each so the code means something,
 * and still allows a hand-entered value for a zone with no activity yet.
 */

interface ZonePickerProps {
  /** Selected zones. Single-select passes at most one. */
  value: string[]
  onChange: (zones: string[]) => void
  multiple?: boolean
  label?: string
  hint?: string
}

const ZONE_FORMAT = /^ZONE_-?\d+(\.\d+)?_-?\d+(\.\d+)?$/

export default function ZonePicker({
  value,
  onChange,
  multiple = true,
  label = 'Service zones',
  hint,
}: ZonePickerProps) {
  const [zones, setZones] = useState<ZoneSummary[]>([])
  const [loading, setLoading] = useState(true)
  const [search, setSearch] = useState('')
  const [manual, setManual] = useState('')

  useEffect(() => {
    fetchZones()
      .then((data) => setZones(data?.zones ?? []))
      .catch(() => setZones([]))
      .finally(() => setLoading(false))
  }, [])

  const filtered = useMemo(() => {
    const term = search.trim().toLowerCase()
    if (!term) return zones.slice(0, 30)
    return zones
      .filter(
        (z) =>
          z.zone.toLowerCase().includes(term) ||
          (z.sampleAddress ?? '').toLowerCase().includes(term),
      )
      .slice(0, 30)
  }, [zones, search])

  // A zone can be selected without appearing in the list — an admin may be
  // setting up an area before any work happens there.
  const unlisted = value.filter((zone) => !zones.some((z) => z.zone === zone))

  const toggle = (zone: string) => {
    if (value.includes(zone)) {
      onChange(value.filter((z) => z !== zone))
      return
    }
    onChange(multiple ? [...value, zone] : [zone])
  }

  const addManual = () => {
    const zone = manual.trim()
    if (!zone) return
    if (!ZONE_FORMAT.test(zone)) return
    if (!value.includes(zone)) onChange(multiple ? [...value, zone] : [zone])
    setManual('')
  }

  const manualInvalid = manual.trim().length > 0 && !ZONE_FORMAT.test(manual.trim())

  return (
    <div>
      <label className="block text-xs font-medium text-[#6B7280] mb-1">{label}</label>

      {/* Selected */}
      <div className="flex flex-wrap gap-1.5 mb-2 min-h-[26px]">
        {value.length === 0 ? (
          <span className="text-[11px] text-[#9CA3AF] italic">
            None selected — {multiple ? 'this company wins no zone preference' : 'no zone assigned'}
          </span>
        ) : (
          value.map((zone) => (
            <span
              key={zone}
              className="inline-flex items-center gap-1 text-[10px] font-semibold px-2 py-0.5 rounded-full bg-blue-50 text-blue-700"
            >
              <MapPin size={9} />
              {zone}
              {unlisted.includes(zone) && <span className="text-[9px] text-blue-400">(new)</span>}
              <button
                type="button"
                onClick={() => toggle(zone)}
                className="hover:text-blue-900"
                aria-label={`Remove ${zone}`}
              >
                <X size={9} />
              </button>
            </span>
          ))
        )}
      </div>

      {/* Search + list */}
      <input
        type="text"
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        placeholder="Search zones by code or address..."
        className="w-full border border-gray-200 rounded-xl px-3 py-2 text-sm text-[#1A1A2E] placeholder-[#9CA3AF] focus:outline-none focus:ring-2 focus:ring-primary-500 mb-2"
      />

      <div className="border border-gray-100 rounded-xl max-h-40 overflow-y-auto divide-y divide-gray-50">
        {loading ? (
          <div className="flex items-center justify-center py-6">
            <Loader2 className="w-4 h-4 animate-spin text-primary-600" />
          </div>
        ) : filtered.length === 0 ? (
          <p className="text-[11px] text-[#9CA3AF] italic px-3 py-4 text-center">
            {zones.length === 0
              ? 'No zones yet — nothing has been collected anywhere.'
              : 'No zone matches that search.'}
          </p>
        ) : (
          filtered.map((zone) => {
            const selected = value.includes(zone.zone)
            return (
              <button
                key={zone.zone}
                type="button"
                onClick={() => toggle(zone.zone)}
                className={`w-full text-left px-3 py-2 hover:bg-gray-50 transition-colors ${selected ? 'bg-primary-50' : ''}`}
              >
                <p className="text-xs font-semibold text-[#1A1A2E] flex items-center gap-1.5">
                  <MapPin size={10} className={selected ? 'text-primary-600' : 'text-gray-400'} />
                  {zone.zone}
                  {selected && <span className="text-[9px] text-primary-600">selected</span>}
                </p>
                <p className="text-[10px] text-[#6B7280] ml-4">
                  {zone.sampleAddress ?? 'no recorded address'}
                  {' · '}
                  {zone.collectors} collector{zone.collectors === 1 ? '' : 's'}
                  {' · '}
                  {zone.pickups} pickup{zone.pickups === 1 ? '' : 's'}
                </p>
              </button>
            )
          })
        )}
      </div>

      {/* Manual entry, for a zone with no activity yet */}
      <div className="flex gap-2 mt-2">
        <input
          type="text"
          value={manual}
          onChange={(e) => setManual(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault()
              addManual()
            }
          }}
          placeholder="Or type a zone: ZONE_5.6_-0.18"
          className={`flex-1 border rounded-xl px-3 py-1.5 text-xs text-[#1A1A2E] placeholder-[#9CA3AF] focus:outline-none focus:ring-2 ${
            manualInvalid
              ? 'border-red-300 focus:ring-red-400'
              : 'border-gray-200 focus:ring-primary-500'
          }`}
        />
        <button
          type="button"
          onClick={addManual}
          disabled={manualInvalid || manual.trim().length === 0}
          className="flex items-center gap-1 text-xs font-semibold text-primary-700 bg-primary-50 border border-primary-200 px-3 py-1.5 rounded-xl hover:bg-primary-100 transition disabled:opacity-40"
        >
          <Plus size={12} />
          Add
        </button>
      </div>
      {manualInvalid && (
        <p className="text-[10px] text-red-600 mt-1">
          Zones look like ZONE_5.6_-0.18 — the grid cell for a latitude and longitude.
        </p>
      )}
      {hint && !manualInvalid && <p className="text-[10px] text-[#9CA3AF] mt-1">{hint}</p>}
    </div>
  )
}
